import {
  CopilotChatConfigurationProvider,
  CopilotKit,
  CopilotSidebar,
  ToolCallStatus,
  useHumanInTheLoop,
  useInterrupt,
} from "@copilotkit/react-core/v2";
import "@copilotkit/react-core/v2/styles.css";

import {
  COPILOT_AGENT_ID,
  COPILOT_LABELS,
  COPILOT_RUNTIME_URL,
  OFFER_CHOICES_TOOL,
} from "@/constants/copilot/copilot-constants";
import { useCopilotAccessToken } from "@/hooks/copilot/use-copilot-access-token";

import { ApprovalCards } from "./approval-card";
import { ChoiceCard } from "./choice-card";
import { COPILOT_MARKDOWN } from "./copilot-markdown";
import { COPILOT_TOOL_RENDERERS } from "./tool-renderers";

// Compact chat for the side panel, with CopilotKit's own options: no copy row under replies, no copy/edit row under
// the user's messages (conversations aren't kept, so editing/branching doesn't apply), and CopilotKit's cpk:pt-10
// above each user message swapped for its own smaller spacing classes (its twMerge replaces them). Reasoning is
// never shown: the model's thinking arrives only as an encrypted marker, which CopilotKit would draw as an empty
// "Thought for Ns" header.
const COPILOT_MESSAGE_VIEW = {
  assistantMessage: { markdownRenderer: COPILOT_MARKDOWN, toolbarVisible: false },
  userMessage: { className: "cpk:pt-1 cpk:mt-3", toolbar: () => null },
  reasoningMessage: { className: "hidden" },
};

function Copilot() {
  useInterrupt({
    render: ({ interrupts, resolve }) => <ApprovalCards changes={interrupts} resolve={resolve} />,
  });

  // The agent's questions as a card it writes itself (one tap, radio, checkboxes, "Other"). If the person types
  // instead of tapping, the pending run is aborted and the tool records an error result, so the conversation simply
  // carries on with the new message.
  useHumanInTheLoop({
    ...OFFER_CHOICES_TOOL,
    render: ({ args, status, respond, result }) => (
      <ChoiceCard
        question={args.question}
        options={args.options}
        multiple={args.multiple}
        allowOther={args.allow_other}
        respond={status === ToolCallStatus.Executing ? respond : undefined}
        result={result}
      />
    ),
  });

  return <CopilotSidebar defaultOpen={false} messageView={COPILOT_MESSAGE_VIEW} />;
}

export function CopilotPanel() {
  const token = useCopilotAccessToken();
  if (!token) return null;

  return (
    <CopilotKit
      runtimeUrl={COPILOT_RUNTIME_URL}
      // The runtime serves CopilotKit's multi-route endpoints (createCopilotEndpoint's default mode).
      useSingleEndpoint={false}
      renderToolCalls={COPILOT_TOOL_RENDERERS}
      headers={{ Authorization: `Bearer ${token}` }}
    >
      <CopilotChatConfigurationProvider agentId={COPILOT_AGENT_ID} labels={COPILOT_LABELS}>
        <Copilot />
      </CopilotChatConfigurationProvider>
    </CopilotKit>
  );
}
